import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { useMemberManagement } from '@/hooks/useMemberManagement';
import { Card } from '@/imports/ui/chakra/components/Card';
import { REG_EXP } from '@/utilities';

export type PersonalDetailsFormValues = {
  firstName?: string;
  lastName?: string;
  contactEmail?: string;
  primaryPhone?: string;
  jobTitle?: string;
  company?: string;
  addressLine0?: string;
  addressLocality?: string;
  addressAdministrativeArea?: string;
  addressRegionCode?: string;
  addressPostalCode?: string;
  oldview?: boolean;
};

export const PersonalDetailsForm: React.FC<{
  onBlurContactEmail?: () => void;
  isLoading: boolean;
  twoColumn?: boolean;
  oldview?: boolean;
}> = ({ onBlurContactEmail, isLoading, twoColumn=false, oldview=true }) => {
  const memberManagement = useMemberManagement();
  const {
    register,
    formState: { errors },
  } = useFormContext<PersonalDetailsFormValues>();

  return (
    <Card p={oldview ? '0' : '6'} border={oldview ? '0' : 'undefined'} boxShadow={oldview ? 'none' : 'base'}>
      <Stack spacing="6" >
       {!twoColumn && <Text textStyle="xlBodyStrong">Personal Details</Text> }
        <Grid templateColumns={twoColumn ? { base: '1fr', md: '1fr 1fr' } : '1fr'} gap="6">

        <FormControl
          isRequired
          isInvalid={!!errors.firstName}
          isDisabled={isLoading}
        >
          <FormLabel>First Name</FormLabel>
          <Input
            placeholder="First Name"
            {...register('firstName', { required: 'This is a required field' })}
          />
          <FormErrorMessage>{errors.firstName?.message}</FormErrorMessage>
        </FormControl>

        <FormControl
          isRequired
          isInvalid={!!errors.lastName}
          isDisabled={isLoading}
        >
          <FormLabel>Last Name</FormLabel>
          <Input
            placeholder="Last Name"
            {...register('lastName', { required: 'This is a required field' })}
          />
          <FormErrorMessage>{errors.lastName?.message}</FormErrorMessage>
        </FormControl>

        {memberManagement.isActive && (
          <FormControl isInvalid={!!errors.contactEmail} isDisabled={isLoading}>
            <FormLabel>Contact Email</FormLabel>
            <Input
              placeholder="name@gmail.com"
              {...register('contactEmail', {
                onBlur: onBlurContactEmail,
                validate: {
                  email: (value) =>
                    !value ||
                    REG_EXP.EmailWithTLD.test(value) ||
                    'You must enter a valid email address',
                },
              })}
            />
            <FormErrorMessage>{errors.contactEmail?.message}</FormErrorMessage>
          </FormControl>
        )}

        <FormControl isDisabled={isLoading}>
          <FormLabel>Primary Phone</FormLabel>
          <Input placeholder="(000) 000-0000" {...register('primaryPhone')} />
        </FormControl>

        <FormControl isDisabled={isLoading}>
          <FormLabel>Company</FormLabel>
          <Input placeholder="Company name" {...register('company')} />
        </FormControl>

        <FormControl isDisabled={isLoading}>
          <FormLabel>Job Title</FormLabel>
          <Input placeholder="Job title" {...register('jobTitle')} />
        </FormControl>
         </Grid>

        <FormControl isDisabled={isLoading}>
          <FormLabel>Street Address</FormLabel>
          <Input placeholder="Street Address" {...register('addressLine0')} />
        </FormControl>
     
        <Stack direction="row" spacing="4">
          <FormControl isDisabled={isLoading}>
            <FormLabel>City</FormLabel>
            <Input placeholder="City" {...register('addressLocality')} />
          </FormControl>
          <FormControl>
            <FormLabel>State / Province / Region</FormLabel>
            <Input
              placeholder="State / Province / Region"
              {...register('addressAdministrativeArea')}
            />
          </FormControl>
        </Stack>

        <Stack direction="row" spacing="4">
          <FormControl isDisabled={isLoading}>
            <FormLabel>Country</FormLabel>
            <Input placeholder="Country" {...register('addressRegionCode')} />
          </FormControl>

          <FormControl isDisabled={isLoading}>
            <FormLabel>Postal / Zip Code</FormLabel>
            <Input
              placeholder="Postal / Zip Code"
              {...register('addressPostalCode')}
            />
          </FormControl>
        </Stack>
      </Stack>
    </Card>
  );
};
